import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setProfile } from "../../../redux/slice/profileSlice";
import { setSession } from "../../../redux/slice/authSlice";
import { verifyAuthToken } from "../../../services/authService";
import {
  claimEtsyOrder,
  resumeEtsyClaim,
  storeResumeToken,
  readResumeToken,
  clearResumeToken,
  storeEtsyReceiptId,
  clearEtsyFinished,
  finishEtsySession,
} from "../../../services/etsyService";
import { loadUserData, determineUserDestination } from "../../../util/navigationUtils";
import { resetStep } from "../../../redux/slice/stepSlice";
import { clearSketch } from "../../../redux/slice/sketchSlice";
import LoadingFallback from "../../../components/elements/LoadingFallback";
import { AlertCircle } from "lucide-react";
import Button from "../../../components/ui/Button";
import TextField from "../../../components/ui/TextField";
import Checkbox from "../../../components/ui/Checkbox";
import { classNames } from "../../../helpers/classNames";
import { setTitle } from "../../../helpers/MetaTag";

/**
 * /etsy — where an Etsy buyer starts.
 *
 * Etsy inverts the Shopify flow. On Shopify a customer builds their book and
 * then pays, arriving here already signed in. On Etsy they pay first, on Etsy,
 * and we get no email address for them — Etsy does not release one. So this
 * page is the entire front door: it takes an order number plus one detail from
 * the shipping address, and turns that into a session.
 *
 * It is the only unauthenticated entry point in the app, so two things matter:
 *
 * - **A wrong answer must never say why.** The API returns one message for
 *   every failure; this page shows it verbatim and adds nothing. Saying "we
 *   found the order but the name is wrong" would turn order-number guessing
 *   into a way to discover which orders exist.
 *
 * - **Getting back in has to be easy.** With no email there is no reset link,
 *   so a buyer who cannot match is genuinely stuck. Hence a stored resume
 *   token, and a form that accepts either the surname or the postcode.
 */
const EtsyClaim = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [checkingResume, setCheckingResume] = useState(true);
  // The buyer instructions email links here with ?order=<receipt id>. Digits
  // only: anything else is not an Etsy order number, so ignore it.
  const [searchParams] = useSearchParams();
  const linkedOrder = /^\d{6,}$/.test(searchParams.get("order") || "") ? searchParams.get("order") : "";
  const [orderNumber, setOrderNumber] = useState(linkedOrder);
  const [nameOrZip, setNameOrZip] = useState("");
  const [email, setEmail] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /** Establish the session a successful claim returns, then go to the wizard. */
  const enterApp = useCallback(
    async (result) => {
      storeResumeToken(result.resumeToken);
      // Marks the rest of this session as an Etsy build, which is what makes the
      // last wizard step offer "send to print" instead of a checkout the buyer
      // has already paid for.
      storeEtsyReceiptId(result.receiptId);
      // A fresh claim in a tab that finished an earlier build: that build's
      // "no longer available" state must not follow this one around.
      clearEtsyFinished();
      localStorage.setItem("auth_token", result.accessToken);

      const body = await verifyAuthToken();
      if (!body || body.status !== 200 || !body.data) {
        // The token we were just handed did not verify. Nothing sensible left
        // to try, and silently looping the form would be baffling.
        setError("We couldn't sign you in. Please try again in a moment.");
        setSubmitting(false);
        return;
      }

      const authUser = body.data;
      const accessToken = localStorage.getItem("auth_token");
      dispatch(setProfile({ ...authUser, access_token: accessToken }));
      dispatch(
        setSession({
          user: { ...authUser, access_token: accessToken },
          status: "authorized",
          error: null,
        })
      );

      let userData = null;
      try {
        userData = await loadUserData(authUser._id, dispatch, false);
      } catch (error) {
        // Handled below: the user area retries the load once we navigate there.
        console.error("Loading user data after Etsy claim failed:", error);
      }

      // An Etsy buyer goes straight into the wizard, never to My Books. They
      // arrived from one Etsy order to build one book, so a library screen is a
      // dead end — and on a first visit there are no books to list anyway.
      //
      // The question that decides where they land is "does THIS order have a
      // book yet", which the API answers. Routing on "does the buyer have any
      // books" would be wrong for a repeat customer: their second order would
      // drop them back into the finished book from their first, instead of
      // starting the one they just paid for.
      if (!result.bookId) {
        // Clear the wizard first, exactly as My Books' "create new" does.
        // loadUserData above ran while we were still on /etsy, so its
        // "don't pick a book on /create-book" guard did not apply and it
        // selected the buyer's PREVIOUS unfinished book. Left in place, step 1
        // opens pre-filled with that book's title and saves over it, so the
        // order they just paid for never gets a book of its own.
        dispatch(resetStep());
        dispatch(clearSketch());
        navigate("/create-book?stepCode=Create_New_Book", { replace: true });
        return;
      }

      // Their books didn't load. Go into the user area, which retries the load
      // and then resumes the right step; never guess from an empty book list.
      if (!userData) {
        navigate("/review?stepCode=Review_Your_Book", { replace: true });
        return;
      }

      // This order's book was already sent to print: nothing is left to do, so
      // show the same confirmation as right after they sent it. Taking them into
      // the app instead lands a finished buyer on My Books with Shopify buttons.
      const thisOrdersBook = userData.myBooks.find((book) => book._id === result.bookId);
      if (thisOrdersBook?.isOrderPlaces) {
        finishEtsySession({ format: result.format, copies: result.copies });
        return;
      }

      // This order already has a book — resume wherever they left off. The
      // dashboard fallback is remapped to the last step, since for an Etsy
      // buyer "finished" means "look at your book", not "browse your library".
      const { profile, myBooks, activeBookId } = userData;
      const destination = determineUserDestination(profile, myBooks, activeBookId);
      navigate(destination === "/dashboard" ? "/review?stepCode=Review_Your_Book" : destination, {
        replace: true,
      });
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    setTitle("Let's build your book | MemoryDoodle");
  }, []);

  // Returning visitor: try the stored token before showing the form at all.
  useEffect(() => {
    let cancelled = false;

    const tryResume = async () => {
      const token = readResumeToken();
      if (!token) {
        if (!cancelled) setCheckingResume(false);
        return;
      }
      const result = await resumeEtsyClaim(token);
      if (cancelled) return;

      // A link for a different order (a repeat buyer's new purchase) must not
      // resume the old one: show the form, pre-filled with the new order.
      const forLinkedOrder = !linkedOrder || String(result && result.receiptId) === linkedOrder;
      if (result && result.ok && forLinkedOrder) {
        await enterApp(result);
        return;
      }
      if (result && result.ok) {
        setCheckingResume(false);
        return;
      }
      // Expired or no longer valid: drop it and fall back to the form rather
      // than retrying a token that will keep failing.
      if (!result || !result.networkError) clearResumeToken();
      setCheckingResume(false);
    };

    tryResume();
    return () => {
      cancelled = true;
    };
  }, [enterApp, linkedOrder]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setSubmitting(true);

    // One field accepts either answer, so the buyer does not have to work out
    // which we want. Digits look like a postcode; anything else is a name.
    const trimmed = nameOrZip.trim();

    if (!EMAIL_PATTERN.test(email.trim())) {
      setError("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }
    const looksLikeZip = /^[0-9][0-9\s-]*$/.test(trimmed);

    const result = await claimEtsyOrder({
      orderNumber,
      name: looksLikeZip ? "" : trimmed,
      zip: looksLikeZip ? trimmed : "",
      email: email.trim(),
      marketingOptIn,
    });

    if (!result || !result.ok) {
      setError((result && result.message) || "Something went wrong. Please try again.");
      setSubmitting(false);
      return;
    }

    await enterApp(result);
  };

  if (checkingResume) return <LoadingFallback />;

  // ---- on the redesign (docs/design/HANDOFF.md: EtsyClaim, TabEtsyClaim, DeskEtsyClaim)
  const canSubmit = !submitting && !!orderNumber.trim() && !!nameOrZip.trim() && !!email.trim();

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-7 md:py-10 lg:max-w-[1040px] lg:px-8 lg:py-12">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-10">
      <form onSubmit={onSubmit} noValidate className="flex min-w-0 flex-col gap-5 lg:flex-[999_1_480px]">
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Let's build your book</h1>
          <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">Thanks for your Etsy order. Find it below and we'll walk you through making your book.</p>
        </div>
        {/* Tablet and desktop (TabEtsyClaim, DeskEtsyClaim): the fields sit in a white card. */}
        <div className="flex flex-col gap-5 md:rounded-[20px] md:border md:border-md-pink-light-1 md:bg-white md:p-8">

        {error ? (
          // The API's one message for every failure, shown as is (the reason is never said).
          <div role="alert" className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm leading-snug text-red-900">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-700" aria-hidden="true" />
            <span>
              {/* Etsy buyers are helped through Etsy (decided 2026-10-23), as on the "on its way" page. */}
              {error} Still stuck? Message us on Etsy.
            </span>
          </div>
        ) : null}

        <TextField
          id="etsy-order-number"
          label="Etsy order number"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="e.g. 4145811365"
          disabled={submitting}
          helper={
            <span className="flex flex-wrap items-center gap-x-2">
              <span>On your Etsy receipt or confirmation email.</span>
              <button type="button" onClick={() => setHelpOpen((v) => !v)} aria-expanded={helpOpen} className="font-semibold text-md-pink-dark underline underline-offset-2">
                Where's this?
              </button>
            </span>
          }
        />
        {helpOpen ? (
          <div className="-mt-2 rounded-xl border border-md-pink-light-1 bg-white px-3.5 py-3 text-sm leading-snug text-[#4A4446]">
            In the Etsy app or site, open <strong>Purchases</strong>, then tap this order. The order number is the long number near the top. It's also in the subject line of your Etsy confirmation email.
          </div>
        ) : null}

        <div className="flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-4">
        <TextField
          id="etsy-name-or-zip"
          label="Last name or ZIP code on the order"
          value={nameOrZip}
          onChange={(e) => setNameOrZip(e.target.value)}
          autoComplete="off"
          placeholder="e.g. Smith or 80301"
          disabled={submitting}
          helper="Whichever is easier. This keeps your order private."
        />

        <TextField
          id="etsy-email"
          type="email"
          inputMode="email"
          label="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          disabled={submitting}
          helper="Use the same email as your Etsy order."
        />
        </div>

        <Checkbox id="etsy-marketing-opt-in" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} disabled={submitting}>
          Email me gift ideas and special offers
        </Checkbox>

        <Button type="submit" size="lg" fullWidth busy={submitting} disabled={!canSubmit}>
          {submitting ? "Finding your order" : "Let's get started"}
        </Button>
        </div>

        <p className="m-0 mt-4 text-xs leading-snug text-md-muted">
          Etsy is a trademark of Etsy, Inc. This application uses the Etsy API but is not endorsed or certified by Etsy, Inc.
        </p>
      </form>
      <aside className="hidden lg:flex lg:flex-[1_1_300px] lg:max-w-[320px] lg:flex-col lg:gap-4 lg:rounded-[20px] lg:border lg:border-md-pink-light-1 lg:bg-white lg:p-7">
        <h2 className="m-0 font-sans text-[13px] font-bold uppercase tracking-[0.06em] text-md-muted">What happens next</h2>
        <ol className="m-0 flex list-none flex-col gap-4 p-0">
          {[
            ["Find your order", "We match it to your Etsy purchase."],
            ["Make your book", "Add a title, a cover photo and 20 pages."],
            ["We print and ship it", "Etsy emails you tracking when it ships."],
          ].map(([title, body], i) => (
            <li key={title} className="flex items-start gap-3.5">
              <span aria-hidden="true" className={classNames("mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold", i === 0 ? "bg-md-pink-dark text-white" : "bg-md-pink/10 text-md-pink-darker")}>
                {i + 1}
              </span>
              <span className="flex flex-col gap-0.5">
                <span className="text-base font-bold text-md-dark">{title}</span>
                <span className="text-sm leading-snug text-md-muted">{body}</span>
              </span>
            </li>
          ))}
        </ol>
      </aside>
      </div>
    </div>
  );
};

/** Same shape the API's user schema enforces; the server re-checks it. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default EtsyClaim;
